import type { ComponentProps } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import type { BorderRadiusProps, WidthProps } from 'styled-system';
import { borderRadius, color, themeGet, width } from 'styled-system';

import type { FlexShrinkProps, TextDecorationProps } from '../../theme/system-utilities';
import { flexShrink, textDecoration } from '../../theme/system-utilities';
import { dispatchTrackingEventGA4 } from '../../tracking/dispatchTrackingEventGA4';
import { Element } from '../../types/tracking';
import { SubtitleST4 } from '../Typography';
import { ButtonBase } from './Base';
import type { ButtonTrackingProp } from './index';

const Base = styled(ButtonBase)<
  WidthProps & BorderRadiusProps & TextDecorationProps & FlexShrinkProps
>`
  ${color};
  ${width};
  ${flexShrink};
  ${borderRadius};
  ${textDecoration};

  &:hover {
    color: ${themeGet('colors.sys.neutral.text.hover')};
    border-color: ${themeGet('colors.sys.neutral.border.hover')};
  }

  &:active {
    color: ${themeGet('colors.sys.neutral.text.active')};
    border-color: ${themeGet('colors.sys.neutral.border.active')};
  }
`;

type RoundedButtonProps = ComponentProps<typeof Base> & ButtonTrackingProp;

interface RoundedButtonLinkProps extends RoundedButtonProps {
  href: string;
}

const RoundedButton = ({ children, tracking, onClick, ...props }: RoundedButtonProps) => {
  const handleClick = (evt: React.MouseEvent<HTMLButtonElement>) => {
    if (tracking) {
      const { element = Element.Button, text, eventName, ...restTrackingParams } = tracking;
      dispatchTrackingEventGA4({
        event: eventName,
        element,
        [`${element}_text`]: text,
        ...restTrackingParams,
      });
    }
    onClick?.(evt);
  };

  return (
    <Base
      py="sp_4"
      px="sp_12"
      borderWidth={1}
      borderStyle="solid"
      width="fit-content"
      textDecoration="none"
      borderRadius="x-large"
      color="sys.neutral.text.default"
      borderColor="sys.neutral.border.default"
      onClick={handleClick}
      {...props}
    >
      <SubtitleST4 color="inherit">{children}</SubtitleST4>
    </Base>
  );
};

const RoundedButtonLink = (props: RoundedButtonLinkProps) => <RoundedButton as="a" {...props} />;

export { RoundedButtonLink };
export default RoundedButton;
